import { indoorVisibleCategoriesAtom } from "@/atoms/indoor";
import { FloatingFullscreenButton } from "@/components";
import { CategoryGrid } from "@/components/home/CategoryGrid";
import { CategoryModal } from "@/components/modals/CategoryModal";
import { ItemModal } from "@/components/modals/ItemModal";
import { useIndoorMerchantStatus } from "@/hooks/use-indoor-merchant-status";
import { useItemModalParam } from "@/hooks/use-item-modal-param";
import { EmptyState, PageContainer, SearchEmpty } from "@/components/ui";
import { BasicInfo, Category } from "@/types";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MenuItemModalContent } from "../item/item-modal-content";
import { MenuHeader } from "../menu-header";
import { MenuTopBar } from "../menu-top-bar";
import { MenuCategoryCard } from "./category-card";
import { MenuCategoryModalContent } from "./category-modal-content";

/**
 * Indoor CATEGORY_LIST menu view. Visually identical to delivery
 * ImageMenu/CategoryMenu (category image grid + full-screen modal). No cart.
 *
 * Search: header term filters categories by name/description — same UX as
 * delivery CategoryMenu.
 */
export function MenuImageMenu({
  basicInfo,
  categories,
}: {
  basicInfo: BasicInfo;
  categories: Category[];
}) {
  const availableCategories = useAtomValue(indoorVisibleCategoriesAtom);
  const { merchantIsOpen } = useIndoorMerchantStatus();

  const [itemsFilter, setItemsFilter] = useState("");

  const filteredCategories = useMemo(() => {
    const term = itemsFilter.trim().toUpperCase();
    if (!term) return availableCategories;
    return availableCategories.filter(
      (c) =>
        c.name.toUpperCase().includes(term) ||
        (c.description && c.description.toUpperCase().includes(term)),
    );
  }, [itemsFilter, availableCategories]);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categorySearch = searchParams.get("category");
  const { itemOfferId, openItem: handleItemClick, closeItem: handleItemModalClose } =
    useItemModalParam();

  const modalContentRef = useRef<HTMLDivElement>(null);
  const itemModalContentRef = useRef<HTMLDivElement>(null);

  const [category, setCategory] = useState<Category | undefined>();

  useEffect(() => {
    setCategory(categories.find((c) => c.id === categorySearch));
  }, [categorySearch, categories]);

  function handleModalClose() {
    navigate("/menu");
  }

  const isSearching = itemsFilter.trim().length > 0;

  return (
    <>
      <MenuTopBar
        basicInfo={basicInfo}
        searchValue={itemsFilter}
        onSearchChange={setItemsFilter}
        searchPlaceholder="Buscar categorias..."
      />
      <MenuHeader basicInfo={basicInfo} />
      <PageContainer gutter="none" surface="card" paint className="py-4">
        <CategoryGrid>
          {filteredCategories.length > 0 ? (
            filteredCategories.map((c) => <MenuCategoryCard key={c.id} category={c} />)
          ) : isSearching ? (
            <SearchEmpty
              className="col-span-full"
              title="Nenhuma categoria encontrada"
              description={`Não encontramos categorias com o termo "${itemsFilter}".`}
            />
          ) : (
            <EmptyState
              size="section"
              className="col-span-full"
              title={merchantIsOpen ? "Ainda não temos itens disponíveis" : "Cardápio fora do horário"}
              description="Estamos preparando o cardápio. Volte em breve!"
            />
          )}
        </CategoryGrid>
      </PageContainer>

      <FloatingFullscreenButton />

      <CategoryModal
        open={!!categorySearch && !!category}
        contentRef={modalContentRef}
        onClose={handleModalClose}
      >
        {category && (
          <MenuCategoryModalContent
            category={category}
            handleModalClose={handleModalClose}
            handleItemClick={handleItemClick}
            modalContentRef={modalContentRef}
          />
        )}
      </CategoryModal>

      <ItemModal
        open={itemOfferId !== ""}
        onClose={handleItemModalClose}
        contentRef={itemModalContentRef}
      >
        <MenuItemModalContent
          onClose={handleItemModalClose}
          itemOfferId={itemOfferId}
          handleItemClick={handleItemClick}
          contentRef={itemModalContentRef}
        />
      </ItemModal>
    </>
  );
}
